import { debounce, reduce } from 'lodash';
import React, { FunctionComponent, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState, WheelEventHandler } from 'react';
import styled from '../../themes/themed-styled-components';

const Container = styled.div<{ containScroll: boolean }>`
    width: 100%;
    overflow: hidden;
    overflow-x: scroll;

    -ms-overflow-style: none;
    scrollbar-width: none; /* Firefox */

    &::-webkit-scrollbar {
      display: none;
    }

    overscroll-behavior-y: ${({ containScroll }) => containScroll ? 'contain' : 'auto'};
`;

const Content = styled.div`
    width: max-content;
    min-width: 100%;
`;

type HorizontalScrollProps = PropsWithChildren<{
    className?: string | null
}>

const HorizontalScroll: FunctionComponent<HorizontalScrollProps> = ({ children }) => {

    const containerRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);

    const widths = useRef<[number, number]>([0, 0]);

    const [containScroll, setContainScroll] = useState<boolean>(false);

    const setWidthsDebounced = useMemo(() => debounce((newWidths: [number, number]) => {
        if (containerRef.current === null || contentRef.current === null) {
            return;
        }
        widths.current = newWidths;

        const [containerWidth, contentWidth] = widths.current;
        setContainScroll(contentWidth > containerWidth);
    }, 500), [setContainScroll]);

    const resizeObserverCallback = useCallback((entries: ResizeObserverEntry[]) => {
        const container = containerRef.current;
        const content = contentRef.current;

        setWidthsDebounced(reduce(entries, (widths, { target, contentBoxSize, borderBoxSize }) => {

            switch (target) {
                case container:
                    widths[0] = ((Array.isArray(contentBoxSize) ? contentBoxSize[0] : contentBoxSize) as ResizeObserverSize).inlineSize;
                    break;
                case content:
                    widths[1] = ((Array.isArray(borderBoxSize) ? borderBoxSize[0] : borderBoxSize) as ResizeObserverSize).inlineSize;
                    break;
            }

            return widths;
        }, [...widths.current]));
    }, [setWidthsDebounced]);

    useEffect(() => {
        const container = containerRef.current;
        const content = contentRef.current;

        if (container === null || content === null) {
            return;
        }

        const observer = new ResizeObserver(resizeObserverCallback);

        observer.observe(container);
        observer.observe(content);

        return () => {
            observer.disconnect();
        }
    }, [resizeObserverCallback]);

    const handleWheel: WheelEventHandler<HTMLElement> = useCallback((e) => {
        const container = containerRef.current;
        container?.scroll({ left: container.scrollLeft + e.deltaY / 5, behavior: 'smooth' })
    }, []);

    return <Container ref={containerRef} onWheel={handleWheel} containScroll={containScroll}>
        <Content ref={contentRef}>
            {children}
        </Content>
    </Container>
}
export default HorizontalScroll;
